import React, { useMemo } from 'react';
import { HbrDomTable } from '@harbor/elements-react-standalone';
import {
  Column,
  ColumnAlignment,
  ColumnType,
  DataType,
  getColumnTypeConfig,
} from '../../../types/table';
import { OrgChartRow } from '../../../support/orgScope';
import { directReportEnablement } from '../../../support/enablementLeader';
import type { EducationDrillNode } from '../../../hooks/education-completion-hooks';
import type { JourneyMapAssignmentRow } from '../../../hooks/enablement-journey-map-hooks';

/**
 * Team rollup for the Leader Enablement tab. One row per direct report of the selected manager; each
 * row's figures are role-appropriate — a sub-manager reads "users complete of users" over their whole
 * subtree, an IC reads their own "complete of assigned". Clicking a name drills into that person (a
 * manager opens their team, an IC opens their individual page), exactly like the Campaigns table. No
 * Notify column — this surface is reporting-only.
 */

interface Props {
  orgRows: OrgChartRow[];
  managerEmail: string;
  eduNode: EducationDrillNode | null;
  byEmail: Map<string, JourneyMapAssignmentRow[]>;
  onSelectEmail: (email: string) => void;
}

type RowModel = {
  id: string;
  email: string;
  is_manager: boolean;
  education: string;
  journey: string;
  journeyRate: string;
};

function safeIdFromEmail(email: string) {
  return `u_${email.toLowerCase().replace(/[^a-z0-9_-]/g, '_')}`;
}

/** The hover title for a numeric cell — reads by column and by whether the report is a sub-manager. */
function cellTitle(columnId: string, isManager: boolean): string {
  switch (columnId) {
    case 'education':
      return isManager
        ? 'Education series completed of assigned, across the subtree'
        : 'Education series completed of assigned';
    case 'journey':
      return isManager ? 'Users completed of team ICs' : 'Journey maps completed of assigned';
    case 'journeyRate':
      return isManager ? 'Map completion % across the subtree' : 'Map completion %';
    default:
      return '';
  }
}

export default function EnablementTeamMembersTable({
  orgRows,
  managerEmail,
  eduNode,
  byEmail,
  onSelectEmail,
}: Props) {
  const normalizedManagerEmail = String(managerEmail || '').toLowerCase();
  // With no education node (Snowflake/Mindtickle unavailable) a manager's education cell would read a
  // falsely-precise "0 of N" — N ICs, 0 complete only because there's no data to prove otherwise. Show
  // "—" instead so the column matches the "education unavailable" banner rather than looking like data.
  const hasEducation = eduNode != null;

  const data = useMemo<RowModel[]>(() => {
    return directReportEnablement(normalizedManagerEmail, orgRows, eduNode, byEmail).map((r) => {
      return {
        id: safeIdFromEmail(r.email),
        email: r.email,
        is_manager: r.isManager,
        education: hasEducation ? `${r.education.done} of ${r.education.total}` : '—',
        journey: `${r.journey.done} of ${r.journey.total}`,
        journeyRate: `${r.journeyRate}%`,
      };
    });
  }, [normalizedManagerEmail, orgRows, eduNode, byEmail, hasEducation]);

  const visibleColumns: Column[] = useMemo(() => {
    // Percentage widths (summing to 100%) rather than fixed px: HbrDomTable's table is width:100%, so
    // percent columns shrink/grow WITH the container and never force horizontal scroll. Fixed px widths
    // become hard min-widths (see column.service setWidth) whose sum overflowed the screen.
    const custom = (id: string, name: string, width: string, align: ColumnAlignment): Column => ({
      id,
      name,
      width,
      columnType: ColumnType.Custom,
      sortable: false,
      align,
      dataType: DataType.String,
      resizable: false,
      reorderable: false,
    });
    return [
      custom('email', 'Team Member', '40%', ColumnAlignment.Left),
      custom('education', 'Education', '20%', ColumnAlignment.Right),
      custom('journey', 'Journey Maps', '20%', ColumnAlignment.Right),
      custom('journeyRate', 'Journey %', '20%', ColumnAlignment.Right),
    ]
      .map((c) => getColumnTypeConfig(c))
      .map((c) => ({ ...c, resizable: false, reorderable: false }));
  }, []);

  const renderHeader = () =>
    // No `w-max` here: that forced each header to its single-line max-content width, expanding the
    // column past its percentage share. Let the label wrap/ellipsize within the allotted width instead.
    visibleColumns.map((column) => (
      <div key={`column-${column.id}`} slot={`column-${column.id}`}>
        <div
          className="flex items-center"
          style={{ justifyContent: column.align === ColumnAlignment.Right ? 'flex-end' : 'flex-start' }}
        >
          <div>{column.name}</div>
        </div>
      </div>
    ));

  const renderBody = () => {
    if (!data.length) return null;
    const list: JSX.Element[] = [];
    for (const row of data) {
      for (const column of visibleColumns) {
        const slot = `row-${row.id}-column-${column.id}`;

        if (column.id === 'email') {
          list.push(
            <div
              key={slot}
              slot={slot}
              className="min-h-8 min-w-0 flex items-center font-semibold hover:text-link hover:underline cursor-pointer truncate"
              title={`${row.email}${row.is_manager ? ' (Mgr)' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onSelectEmail(row.email);
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSelectEmail(row.email);
                }
              }}
              aria-label={`View ${row.email}`}
            >
              {row.email}
              {row.is_manager ? ' (Mgr)' : ''}
            </div>,
          );
          continue;
        }

        list.push(
          <div
            key={slot}
            slot={slot}
            className="min-h-8 flex items-center box-content"
            style={{ justifyContent: 'flex-end', fontVariantNumeric: 'tabular-nums' }}
            title={cellTitle(column.id, row.is_manager)}
          >
            {(row as any)[column.id]}
          </div>,
        );
      }
    }
    return list;
  };

  if (!data.length) {
    return <div className="hbr-css__text-secondary-sm">No direct reports found.</div>;
  }

  return (
    <HbrDomTable
      autoLayout={true}
      columns={visibleColumns}
      data={data}
      style={{ display: 'block', width: '100%' }}
    >
      {renderHeader()}
      {renderBody()}
    </HbrDomTable>
  );
}
